import { useEffect, useRef, useState } from 'react';
import './render-ai-modify-image.less';
import dayjs from 'dayjs';
import saveAs from 'file-saver';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { IImageLayerProps, ILayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { Loading, message } from '@meitu/xdesign-widgets';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { useStoreState } from '@/store';
import { AIModifyImagePainType } from '@/store/models/plugin-model';
import { Entrance } from '@/types';
import { isFromSafari } from '@/utils/common';
import { openVipModal } from '@/utils/login';
import loadingImg from '../../../assets/AIRepair_00028.png';
import AIRepairIcon from '../../../assets/AIRepair.webp';
import CursorMoveTip from '../../ai-expend-image/expend-operate/cursor-move-tip/cursor-move-tip';
import useShowLayerWaterMark from '../../ai-expend-image/expend-operate/use-show-layer-watermark';
import AddCursor from './add.svg';
import CutCursor from './cut.svg';
import usePain from './use-pain';

export interface ICropImageOperateProps {
    scale: number;
    layer: ILayerProps;
    stageWidth?: number;
    stageHeight?: number;
}

const RenderAiModifyImage = () => {
    const isAiModifyImage = useStoreState((state) => state.pluginModel.isAiModifyImage);

    const node = useRef<HTMLDivElement>(null);
    const painCanvasNode = useRef<HTMLCanvasElement>(null);
    const painMaskCanvasNode = useRef<HTMLCanvasElement>(null);
    const { $t } = useLocaleStore();

    const aiModifyImageInfo = useStoreState((state) => state.pluginModel.aiModifyImageInfo);
    const [wrapSize, setWrapSize] = useState({ width: 0, height: 0 });
    const [scale, setScale] = useState(1);
    const [natureImageSize, setNatureImageSize] = useState({
        naturalWidth: 0,
        naturalHeight: 0,
    });
    const { naturalWidth, naturalHeight } = natureImageSize;
    const mainLayer = editor.active.getMainLayer();
    const activeLayer = editor.active.getPick(true) || mainLayer;
    const centerPoint = usePain(scale, naturalWidth, naturalHeight);
    const [renderUrl, setRenderUrl] = useState('');
    const [showOriginImage, setShowOriginImage] = useState(false);
    const showLayerWaterMark = useShowLayerWaterMark();

    useEffect(() => {
        const getSize = async () => {
            if (!activeLayer) {
                return null;
            }
            Loading.show($t((v) => v.globalMask.loading));
            const { layerType, data } = activeLayer;

            if (![LayerType.MAIN_LAYER, LayerType.IMAGE_LAYER].includes(layerType)) {
                Loading.hidden();
                return null;
            }
            const { url } = data as IImageLayerProps;
            const renderUrl = base64Kit.getLocalUrl(url);
            setRenderUrl(renderUrl);
            const { width, height } = await loadImageAsync(renderUrl);
            setNatureImageSize({ naturalWidth: width, naturalHeight: height });
            Loading.hidden();
        };
        getSize();
    }, []);

    useEffect(() => {
        const activeLayer = editor.active.getPick(true) || mainLayer;

        if (!activeLayer) {
            return;
        }
        const { data, layerType } = activeLayer;

        if (![LayerType.MAIN_LAYER, LayerType.IMAGE_LAYER].includes(layerType)) {
            return;
        }

        const scaleImg = () => {
            const wrapElem = document.getElementById('renderAiModifyImage');
            if (!wrapElem) {
                return;
            }

            const { width: wrapWidth, height: wrapHeight } = wrapElem.getBoundingClientRect();
            setWrapSize({ width: wrapWidth, height: wrapHeight });
            const PADDING = 60;
            if (naturalWidth <= wrapWidth - PADDING / 2 && naturalHeight <= wrapHeight - PADDING / 2) {
                setScale(1);
                return;
            }

            const widthRadio = (wrapWidth - PADDING / 2) / naturalWidth;
            const heightRadio = (wrapHeight - PADDING / 2) / naturalHeight;

            if (widthRadio < heightRadio) {
                setScale(widthRadio);
            } else {
                setScale(heightRadio);
            }
        };
        window.addEventListener('resize', scaleImg);
        scaleImg();

        return () => {
            window.addEventListener('resize', scaleImg);
        };
    }, [isAiModifyImage, naturalWidth, naturalHeight]);

    if (!isAiModifyImage) {
        return null;
    }

    if (!activeLayer) {
        return null;
    }
    const { layerType, data } = activeLayer;

    if (![LayerType.MAIN_LAYER, LayerType.IMAGE_LAYER].includes(layerType)) {
        return null;
    }
    const { url } = data as IImageLayerProps;

    const downloadImg = () => {
        if (showLayerWaterMark) {
            message.info($t((v) => v.header.openVipAndDownload));
            openVipModal({
                entrance: Entrance.Save,
            });
        } else {
            saveAs(
                aiModifyImageInfo.resultUrl,
                `${$t((v) => v.editor.copyRight)}_${$t(
                    (v) => v.collapse.moreCollapse.imageTool.aiModify.title,
                )}_${dayjs().format('YYYY.MM.DD')}.jpg`,
            );
            window._stat_track('aisc_download_success', {
                func: 'image_manipulation',
                is_creative: true,
                download_type: 'direct',
            });
        }
    };

    return (
        <div ref={node} id="renderAiModifyImage" className="c-renderAiModifyImage">
            <div className="bg"></div>
            <div
                className="img-warp"
                style={{
                    width: `${naturalWidth}px`,
                    height: `${naturalHeight}px`,
                    transform: `translate(${(wrapSize.width - naturalWidth * scale) / 2}px, ${
                        (wrapSize.height - naturalHeight * scale) / 2
                    }px) scale(${scale})`,
                    backgroundSize: `${16 / scale}px ${16 / scale}px`,
                    cursor: aiModifyImageInfo.resultUrl ? 'auto' : 'none',
                }}
            >
                <img
                    data-operate-type="AiModifyImage"
                    id="AiModifyImage"
                    src={renderUrl || url}
                    width={naturalWidth}
                    height={naturalHeight}
                    alt="img"
                />

                <canvas
                    id="painCanvas"
                    data-operate-type="AiModifyImage"
                    ref={painCanvasNode}
                    className="painCanvas"
                    width={naturalWidth}
                    height={naturalHeight}
                    style={{
                        visibility: showOriginImage ? 'hidden' : 'visible',
                        cursor: aiModifyImageInfo.resultUrl ? 'auto' : 'none',
                    }}
                />

                <canvas
                    id="painMaskCanvas"
                    data-operate-type="AiModifyImage"
                    ref={painMaskCanvasNode}
                    className="painCanvas painMaskCanvas"
                    width={naturalWidth}
                    height={naturalHeight}
                />

                {aiModifyImageInfo.resultUrl && !showOriginImage ? (
                    <img
                        className="resultImage"
                        src={aiModifyImageInfo.resultUrl}
                        width={naturalWidth}
                        height={naturalHeight}
                        alt="img"
                    />
                ) : null}

                {centerPoint && !aiModifyImageInfo.loading && !aiModifyImageInfo.resultUrl ? (
                    <div
                        className="point"
                        style={{
                            width: `${aiModifyImageInfo.painSize / scale}px`,
                            height: `${aiModifyImageInfo.painSize / scale}px`,
                            left: `${centerPoint.x}px`,
                            top: `${centerPoint.y}px`,
                        }}
                    >
                        <img
                            src={aiModifyImageInfo.painType === AIModifyImagePainType.pain ? AddCursor : CutCursor}
                            alt=""
                        />
                    </div>
                ) : null}
            </div>

            {aiModifyImageInfo.loading ? (
                <div className="loading-wrap">
                    <div className="loading">
                        <img src={isFromSafari() ? loadingImg : AIRepairIcon} alt="loading" />
                        <span>{$t((v) => v.stage.repaintTip)}...</span>
                    </div>
                </div>
            ) : null}

            <CursorMoveTip
                hide={!!(aiModifyImageInfo.loading || aiModifyImageInfo.resultUrl)}
                text={$t((v) => v.stage.paintTip)}
                elemId="painCanvas"
                cursorDistance={24 + 10 + (aiModifyImageInfo.painSize / 100) * 24}
            />

            {aiModifyImageInfo.resultUrl ? (
                <>
                    <div className="downloadResult" onClick={downloadImg}>
                        <IconFont name="DownloadBold" color="#fff" fontSize={16} />
                        <span>{$t((v) => v.stage.downloadResultBtnText)}</span>
                    </div>

                    <div
                        className="compare"
                        onMouseDown={() => setShowOriginImage(true)}
                        onMouseUp={() => setShowOriginImage(false)}
                    >
                        <IconFont name="CompareBold" color="#fff" fontSize={16} />
                        <span>{$t((v) => v.stage.contrast)}</span>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default RenderAiModifyImage;
